import { AuthCallback, useAuth } from '@saay/auth-web';
import { BrandLoader, Button, ErrorState, SessionExpired } from '@saay/ui';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

export const AUTO_LOGIN_STARTED_AT_KEY = 'saay.auth.auto-login-started-at';
export const AUTO_LOGIN_LOOP_GUARD_MS = 30_000;

/** The in-app route (path, query, hash) to come back to after a login redirect. */
export function useCurrentRoute(): string {
  const location = useLocation();
  return `${location.pathname}${location.search}${location.hash}`;
}

/** Starts the Keycloak login and returns to the current route afterwards (FTD §11 session expired). */
export function useLogin(): () => void {
  const { controller } = useAuth();
  const returnTo = useCurrentRoute();
  return useCallback(() => {
    void controller.login({ returnTo });
  }, [controller, returnTo]);
}

function CenteredPage({ children }: { readonly children: ReactNode }) {
  return (
    <main className="saay-center-page" id="main">
      {children}
    </main>
  );
}

/**
 * The brand loader (D-106) while the login is checked or opened: it loops until the session is
 * known, then plays the loaded sequence. Kept in one tree position so the motion never restarts.
 */
function LoginProgress({
  done,
  label,
  onFinished,
}: {
  readonly done: boolean;
  readonly label: string;
  readonly onFinished?: () => void;
}) {
  return (
    <main id="main">
      <BrandLoader
        done={done}
        label={label}
        {...(onFinished === undefined ? {} : { onFinished })}
      />
    </main>
  );
}

function LoginError({ error, onRetry }: { readonly error: unknown; readonly onRetry: () => void }) {
  return (
    <CenteredPage>
      <ErrorState error={error} title="اتصال به سرویس ورود برقرار نشد" />
      <Button onClick={onRetry}>تلاش دوباره</Button>
    </CenteredPage>
  );
}

function recentAutomaticLogin(): boolean {
  try {
    const startedAt = Number(sessionStorage.getItem(AUTO_LOGIN_STARTED_AT_KEY));
    return Number.isFinite(startedAt) && Date.now() - startedAt < AUTO_LOGIN_LOOP_GUARD_MS;
  } catch {
    return false;
  }
}

function markAutomaticLoginStarted(): void {
  try {
    sessionStorage.setItem(AUTO_LOGIN_STARTED_AT_KEY, String(Date.now()));
  } catch {
    // Storage can be disabled; the redirect remains safe because this mount starts it only once.
  }
}

function clearAutomaticLogin(): void {
  try {
    sessionStorage.removeItem(AUTO_LOGIN_STARTED_AT_KEY);
  } catch {
    // Storage can be disabled.
  }
}

/**
 * Renders the app only for an authenticated session (FTD §1.4, §11):
 * - first render: silent re-authentication against the Keycloak SSO session (`prompt=none`);
 * - no session: immediately opens the Keycloak-hosted login, then returns to the same route;
 * - session ended (refresh failed, 401): the session-expired prompt, then back to the same route.
 */
export function AuthGate({ children }: { readonly children: ReactNode }) {
  const { controller, status } = useAuth();
  const returnTo = useCurrentRoute();
  const restoreStarted = useRef(false);
  const loginStarted = useRef(false);
  // Only a page load that finds no session opens the login by itself. A logout in this page ends
  // in `unauthenticated` too, but its end-session redirect must win (a fresh login after a PIN
  // lockout, M-29); the redirect comes back to a fresh load, which then opens the login.
  const sawSession = useRef(false);
  const [error, setError] = useState<unknown>(null);
  // The loaded sequence plays once for every fresh gate. This also covers a callback that has
  // already put the controller into `authenticated` before AuthGate mounts.
  const [introFinished, setIntroFinished] = useState(false);
  const finishIntro = useCallback(() => setIntroFinished(true), []);
  const relogin = useLogin();

  const startLogin = useCallback(
    (automatic: boolean) => {
      if (automatic) {
        markAutomaticLoginStarted();
      }
      setError(null);
      void controller.login({ returnTo }).catch((loginError: unknown) => setError(loginError));
    },
    [controller, returnTo],
  );

  useEffect(() => {
    if (status !== 'initializing' || restoreStarted.current) {
      return;
    }
    restoreStarted.current = true;
    controller.restoreSession(returnTo).catch((restoreError: unknown) => setError(restoreError));
  }, [controller, returnTo, status]);

  useEffect(() => {
    if (status === 'authenticated' || status === 'session-expired') {
      sawSession.current = true;
    }
    if (status === 'authenticated') {
      clearAutomaticLogin();
    }
  }, [status]);

  useEffect(() => {
    if (
      status !== 'unauthenticated' ||
      error !== null ||
      loginStarted.current ||
      sawSession.current
    ) {
      return;
    }
    loginStarted.current = true;
    if (recentAutomaticLogin()) {
      setError(new Error('Automatic login redirect loop prevented.'));
      return;
    }
    startLogin(true);
  }, [error, startLogin, status]);

  if (error !== null) {
    return <LoginError error={error} onRetry={() => startLogin(false)} />;
  }
  if (status === 'initializing' || status === 'unauthenticated') {
    return (
      <LoginProgress
        done={false}
        label={status === 'initializing' ? 'در حال ورود…' : 'در حال رفتن به صفحهٔ ورود…'}
      />
    );
  }
  if (status === 'authenticated' && !introFinished) {
    return <LoginProgress done label="در حال ورود…" onFinished={finishIntro} />;
  }
  if (status === 'session-expired') {
    return (
      <CenteredPage>
        <SessionExpired onLogin={() => relogin()} />
      </CenteredPage>
    );
  }
  return <>{children}</>;
}

/** `/auth/callback`: completes the redirect, then replaces the history entry with the return route. */
export function AuthCallbackRoute() {
  const navigate = useNavigate();
  const { controller } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const onComplete = useCallback(
    (returnTo: string) => {
      void navigate(returnTo, { replace: true });
    },
    [navigate],
  );
  const retry = useCallback(() => {
    setError(null);
    void controller.login({ returnTo: '/' }).catch((loginError: unknown) => setError(loginError));
  }, [controller]);
  if (error !== null) {
    return <LoginError error={error} onRetry={retry} />;
  }
  // The brand loader is the only screen around the login (owner, 2026-10-24): the callback keeps it
  // looping, and its own status line stays hidden because the loader announces the same status.
  return (
    <>
      <LoginProgress done={false} label="در حال ورود…" />
      <div hidden>
        <AuthCallback onComplete={onComplete} onError={setError} />
      </div>
    </>
  );
}
